import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box, Button, Card, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, InputAdornment, MenuItem, Table, TableBody, TableCell,
    TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';

import {
    listEmails, resendEmail, cancelEmail, EMAIL_OUTBOX_STATUS, STATUS_LABEL, STATUS_COLOR, STATUS_FILTERS,
} from '../../services/emailOutbox.service';

/**
 * Email Log — every email the platform sent, who it went to, and whether it
 * went. A failed email keeps each attempt and SendGrid's error.
 *
 * Resend sends the email again exactly as it went out (same subject, body and
 * attachments) and logs it as a new row marked "Resend of …"; the original
 * row keeps its outcome. The admin may change the recipient — the original
 * cc list is then not copied.
 *
 * Held: an email about a school or class that is still a draft waits, and the
 * publish sends it. It cannot be resent (the publish would send it again), but
 * it can be cancelled so the publish leaves it out.
 *
 * Material-UI v4 (this app is not on @mui/material), so styling is `style=`
 * rather than `sx=`.
 */

const fmtDate = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

const StatusPill = ({ status }) => {
    const c = STATUS_COLOR[status] || { fg: '#555', bg: '#eee' };
    return (
        <span style={{
            display: 'inline-block', padding: '3px 10px', borderRadius: 12,
            fontSize: 12, fontWeight: 700, color: c.fg, background: c.bg,
            whiteSpace: 'nowrap',
        }}>
            {STATUS_LABEL[status] || String(status)}
        </span>
    );
};

const Muted = ({ children, style }) => (
    <div style={{ fontSize: 12, color: '#777', ...style }}>{children}</div>
);

const EmailOutbox = () => {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [templates, setTemplates] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('');
    const [template, setTemplate] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // The row whose attempts are open.
    const [details, setDetails] = useState(null);

    // The row being resent, and the address(es) it will go to.
    const [resending, setResending] = useState(null);
    const [resendTo, setResendTo] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [notice, setNotice] = useState('');

    // The held row being cancelled.
    const [cancelling, setCancelling] = useState(null);
    const [cancelError, setCancelError] = useState('');

    /** Guards against an out-of-order response overwriting a newer one. */
    const reqId = useRef(0);

    const load = useCallback(async (params) => {
        const mine = ++reqId.current;
        setLoading(true);
        setError('');
        try {
            const res = await listEmails(params);
            if (mine !== reqId.current) return;
            if (res && res.data) {
                setRows(res.data.emails || []);
                setTotal(Number(res.data.total) || 0);
                if (Array.isArray(res.data.templates)) setTemplates(res.data.templates);
            } else {
                setRows([]);
                setTotal(0);
                setError((res && res.message) || 'Could not load emails.');
            }
        } catch (e) {
            if (mine !== reqId.current) return;
            setRows([]);
            setTotal(0);
            setError((e && e.message) || 'Could not load emails.');
        } finally {
            if (mine === reqId.current) setLoading(false);
        }
    }, []);

    const params = { search, status, template, from_date: fromDate, to_date: toDate, page, rows_per_page: rowsPerPage };

    // Debounced: search runs on the server.
    useEffect(() => {
        const t = setTimeout(() => load(params), 350);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, status, template, fromDate, toDate, page, rowsPerPage, load]);

    const reload = () => load(params);

    const resetPageWith = (setter) => (e) => { setter(e.target.value); setPage(0); };

    const onRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    const openResend = (row) => {
        setResending(row);
        setResendTo((row.to || []).join(', '));
        setSaveError('');
    };

    const originalTo = resending ? (resending.to || []).join(', ') : '';
    const changingRecipient = !!resending && resendTo.trim() !== originalTo;
    const droppingCc = changingRecipient && resending && (resending.cc || []).length > 0;

    const confirmResend = async () => {
        if (!resending) return;
        setSaving(true);
        setSaveError('');
        try {
            const res = await resendEmail({
                email_id: resending.id,
                to: changingRecipient ? resendTo : undefined,
            });
            if (res && res.data) {
                setNotice(`Resent to ${((res.data.email && res.data.email.to) || []).join(', ')}.`);
                setResending(null);
            } else {
                setSaveError((res && res.message) || 'Could not resend that email.');
            }
        } catch (e) {
            // A failed send is still logged as its own row — the reload shows it.
            setSaveError((e && e.message) || 'Could not resend that email.');
        } finally {
            setSaving(false);
            reload();
        }
    };

    const confirmCancel = async () => {
        if (!cancelling) return;
        setSaving(true);
        setCancelError('');
        try {
            const res = await cancelEmail({ email_id: cancelling.id });
            if (res && res.data) {
                setNotice('Cancelled. The publish will not send it.');
                setCancelling(null);
            } else {
                setCancelError((res && res.message) || 'Could not cancel that email.');
            }
        } catch (e) {
            setCancelError((e && e.message) || 'Could not cancel that email.');
        } finally {
            setSaving(false);
            reload();
        }
    };

    const scopeLabel = (s) => {
        if (!s) return '';
        const school = s.institute_code ? `${s.org_code} / ${s.institute_code}` : s.org_code;
        return s.section_code ? `${school} · class ${s.section_code}` : school;
    };

    const filtered = search || status || template || fromDate || toDate;
    const emptyMessage = filtered ? 'No emails match that filter.' : 'No emails have been logged yet.';

    return (
        <Box style={{ padding: 24 }}>
            <Typography variant="h5" style={{ fontWeight: 700, marginBottom: 4 }}>
                Email Log
            </Typography>
            <Typography variant="body2" style={{ color: '#666', marginBottom: 20 }}>
                Every email the platform sends, who it went to, and whether it went. Resend sends the
                same email again and logs it as a new row. Emails are deleted 90 days after they are
                sent, failed or cancelled.
            </Typography>

            {notice ? (
                <Typography style={{ color: '#1b6b3a', background: '#e7f4ec', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontWeight: 600 }}>
                    {notice}
                </Typography>
            ) : null}

            <Card style={{ padding: 16 }}>
                <Box style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                    <TextField
                        value={search}
                        onChange={resetPageWith(setSearch)}
                        placeholder="Search recipient, subject, sender or template"
                        variant="outlined"
                        size="small"
                        style={{ flex: '1 1 320px', maxWidth: 460 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                            ),
                        }}
                    />
                    <TextField
                        select
                        value={status}
                        onChange={resetPageWith(setStatus)}
                        label="Status"
                        variant="outlined"
                        size="small"
                        style={{ width: 160 }}
                    >
                        {STATUS_FILTERS.map((o) => (
                            <MenuItem key={o.value || 'all'} value={o.value}>{o.label}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        value={template}
                        onChange={resetPageWith(setTemplate)}
                        label="Template"
                        variant="outlined"
                        size="small"
                        style={{ width: 280 }}
                    >
                        <MenuItem value="">All templates</MenuItem>
                        {templates.map((t) => (
                            <MenuItem key={t} value={t}>{t}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        type="date"
                        value={fromDate}
                        onChange={resetPageWith(setFromDate)}
                        label="From"
                        variant="outlined"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        type="date"
                        value={toDate}
                        onChange={resetPageWith(setToDate)}
                        label="To"
                        variant="outlined"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                    />
                </Box>

                {error ? (
                    <Typography style={{ color: '#b3261e', fontWeight: 600, padding: '12px 0' }}>
                        {error}
                    </Typography>
                ) : null}

                <TableContainer style={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ fontWeight: 700 }}>Logged</TableCell>
                                <TableCell style={{ fontWeight: 700 }}>To</TableCell>
                                <TableCell style={{ fontWeight: 700 }}>Subject</TableCell>
                                <TableCell style={{ fontWeight: 700 }}>Status</TableCell>
                                <TableCell style={{ fontWeight: 700 }}>Attempts</TableCell>
                                <TableCell style={{ fontWeight: 700 }}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" style={{ padding: 40 }}>
                                        <CircularProgress size={26} />
                                    </TableCell>
                                </TableRow>
                            ) : null}

                            {!loading && rows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" style={{ padding: 40, color: '#777' }}>
                                        {emptyMessage}
                                    </TableCell>
                                </TableRow>
                            ) : null}

                            {!loading && rows.map((r) => (
                                <TableRow key={r.id} hover>
                                    <TableCell style={{ whiteSpace: 'nowrap' }}>
                                        {fmtDate(r.created_at)}
                                        {r.resend_of ? <Muted>Resend{r.resent_by && r.resent_by.email ? ` by ${r.resent_by.email}` : ''}</Muted> : null}
                                    </TableCell>
                                    <TableCell style={{ maxWidth: 260, wordBreak: 'break-word' }}>
                                        <div>{(r.to || []).join(', ') || '—'}</div>
                                        {(r.cc || []).length ? <Muted>cc {r.cc.join(', ')}</Muted> : null}
                                    </TableCell>
                                    <TableCell style={{ maxWidth: 340 }}>
                                        <div style={{ fontWeight: 600 }}>{r.subject || '—'}</div>
                                        <Muted>{r.template || 'No template'}{r.plan_key ? ` · ${r.plan_key}` : ''}</Muted>
                                    </TableCell>
                                    <TableCell>
                                        <StatusPill status={Number(r.status)} />
                                        {Number(r.status) === EMAIL_OUTBOX_STATUS.HELD ? (
                                            <Muted style={{ marginTop: 4, maxWidth: 240 }}>Until {scopeLabel(r.scope)} is published</Muted>
                                        ) : null}
                                        {Number(r.status) === EMAIL_OUTBOX_STATUS.CANCELLED && r.cancel_reason ? (
                                            <Muted style={{ marginTop: 4, maxWidth: 240 }}>{r.cancel_reason}</Muted>
                                        ) : null}
                                        {r.released_at ? <Muted style={{ marginTop: 4 }}>Held, released {fmtDate(r.released_at)}</Muted> : null}
                                        {r.last_error && Number(r.status) !== 5 ? (
                                            <Muted style={{ color: '#a33224', marginTop: 4, maxWidth: 240 }}>{r.last_error}</Muted>
                                        ) : null}
                                        {(r.resent_ids || []).length ? (
                                            <Muted style={{ marginTop: 4 }}>Resent {fmtDate(r.last_resent_at)}</Muted>
                                        ) : null}
                                    </TableCell>
                                    <TableCell>
                                        <Button size="small" onClick={() => setDetails(r)}>
                                            {r.attempt_count} · details
                                        </Button>
                                    </TableCell>
                                    <TableCell style={{ whiteSpace: 'nowrap' }}>
                                        {Number(r.status) === EMAIL_OUTBOX_STATUS.HELD ? (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                style={{ color: '#a33224', borderColor: '#a33224' }}
                                                onClick={() => { setCancelling(r); setCancelError(''); }}
                                            >
                                                Cancel
                                            </Button>
                                        ) : r.resendable ? (
                                            <Button size="small" variant="outlined" color="primary" onClick={() => openResend(r)}>
                                                Resend
                                            </Button>
                                        ) : (
                                            <Tooltip title={r.not_resendable_reason || 'This email cannot be resent.'}>
                                                <span>
                                                    <Button size="small" variant="outlined" disabled>Resend</Button>
                                                </span>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* count comes from the SERVER — the table only ever holds one page. */}
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    onChangePage={(e, p) => setPage(p)}
                    onPageChange={(e, p) => setPage(p)}
                    onChangeRowsPerPage={onRowsPerPage}
                    onRowsPerPageChange={onRowsPerPage}
                />
            </Card>

            <Dialog open={!!details} onClose={() => setDetails(null)} maxWidth="md" fullWidth>
                <DialogTitle>{details ? details.subject : ''}</DialogTitle>
                <DialogContent>
                    {details ? (
                        <>
                            <Typography variant="body2" style={{ marginBottom: 12 }}>
                                <strong>From</strong> {details.from}<br />
                                <strong>To</strong> {(details.to || []).join(', ')}<br />
                                {(details.cc || []).length ? <><strong>Cc</strong> {details.cc.join(', ')}<br /></> : null}
                                <strong>Template</strong> {details.template || '—'}<br />
                                <strong>Sent by</strong> {details.source || '—'}<br />
                                {details.scope ? <><strong>About</strong> {scopeLabel(details.scope)}<br /></> : null}
                                {details.held_at ? <><strong>Held</strong> {fmtDate(details.held_at)}{details.released_at ? ` · released ${fmtDate(details.released_at)}` : ''}<br /></> : null}
                                {details.to_role ? <><strong>Recipient</strong> the school&rsquo;s contact at publish<br /></> : null}
                                {details.cancelled_at ? (
                                    <><strong>Cancelled</strong> {fmtDate(details.cancelled_at)}{details.cancelled_by && details.cancelled_by.email ? ` by ${details.cancelled_by.email}` : ''} — {details.cancel_reason || ''}<br /></>
                                ) : null}
                                <strong>SendGrid id</strong> {details.provider_message_id || '—'}<br />
                                <strong>Deleted on</strong> {details.expires_at ? fmtDate(details.expires_at) : 'not set — kept while it is held'}<br />
                                {(details.attachments || []).length ? (
                                    <><strong>Attachments</strong> {details.attachments.map((a) => a.filename).join(', ')}<br /></>
                                ) : null}
                            </Typography>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell style={{ fontWeight: 700 }}>Attempt</TableCell>
                                        <TableCell style={{ fontWeight: 700 }}>At</TableCell>
                                        <TableCell style={{ fontWeight: 700 }}>Result</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(details.attempts || []).length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={3} style={{ color: '#777' }}>
                                                No attempt has been recorded yet.
                                            </TableCell>
                                        </TableRow>
                                    ) : null}
                                    {(details.attempts || []).map((a, i) => (
                                        <TableRow key={i}>
                                            <TableCell>{i + 1}</TableCell>
                                            <TableCell style={{ whiteSpace: 'nowrap' }}>{fmtDate(a.at)}</TableCell>
                                            <TableCell style={{ color: a.ok ? '#1b6b3a' : '#a33224' }}>
                                                {a.ok ? 'Accepted by SendGrid' : `${a.error_code ? `${a.error_code} · ` : ''}${a.error_message || 'Failed'}`}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetails(null)}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!resending} onClose={() => (saving ? null : setResending(null))} maxWidth="sm" fullWidth>
                <DialogTitle>Resend this email?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 12 }}>
                        {resending ? resending.subject : ''}
                    </Typography>
                    <Typography variant="body2" style={{ color: '#555', marginBottom: 16 }}>
                        The same email — subject, body and attachments as they were sent — goes out again
                        and is logged as a new row. Any link in it is the one originally sent.
                    </Typography>

                    <TextField
                        value={resendTo}
                        onChange={(e) => setResendTo(e.target.value)}
                        label="Send to"
                        helperText="Separate several addresses with commas."
                        variant="outlined"
                        size="small"
                        fullWidth
                    />
                    {droppingCc ? (
                        <Typography variant="body2" style={{ color: '#8a5300', background: '#fff6e5', padding: 12, borderRadius: 6, marginTop: 12 }}>
                            You changed the recipient, so the original cc ({resending.cc.join(', ')}) will not be copied.
                        </Typography>
                    ) : null}

                    {saveError ? (
                        <Typography style={{ color: '#b3261e', fontWeight: 600, marginTop: 12 }}>
                            {saveError}
                        </Typography>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResending(null)} disabled={saving}>Cancel</Button>
                    <Button
                        onClick={confirmResend}
                        disabled={saving || !resendTo.trim()}
                        variant="contained"
                        color="primary"
                    >
                        {saving ? 'Sending…' : 'Resend'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!cancelling} onClose={() => (saving ? null : setCancelling(null))} maxWidth="sm" fullWidth>
                <DialogTitle>Cancel this held email?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 12 }}>
                        {cancelling ? cancelling.subject : ''}
                    </Typography>
                    <Typography variant="body2" style={{ color: '#555' }}>
                        It is waiting for {cancelling ? scopeLabel(cancelling.scope) : ''} to be published.
                        Cancelled, the publish will not send it. This cannot be undone, but a cancelled
                        email can still be resent by hand.
                    </Typography>
                    {cancelError ? (
                        <Typography style={{ color: '#b3261e', fontWeight: 600, marginTop: 12 }}>
                            {cancelError}
                        </Typography>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCancelling(null)} disabled={saving}>Keep it</Button>
                    <Button
                        onClick={confirmCancel}
                        disabled={saving}
                        variant="contained"
                        style={{ background: '#a33224', color: '#fff' }}
                    >
                        {saving ? 'Cancelling…' : 'Cancel email'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default EmailOutbox;
